import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

interface TourStep {
  target: string; // CSS selector or data attribute
  title: string;
  description: string;
  position: "top" | "bottom" | "left" | "right";
}

const TOUR_STEPS: TourStep[] = [
  {
    target: "[data-tour='create-krew']",
    title: "Create a Krew 🎉",
    description: "Start here! Create a group for your next night out — set the vibe, budget, and invite your squad.",
    position: "bottom",
  },
  {
    target: "[data-tour='browse-venues']",
    title: "Browse Venues 🗺️",
    description: "Explore curated nightlife spots. AI will recommend the best matches for your crew's vibe.",
    position: "bottom",
  },
  {
    target: "[data-tour='wallet']",
    title: "Track Your Wallet 💰",
    description: "See all your commitment deposits, refunds, and spending history in one place.",
    position: "bottom",
  },
  {
    target: "[data-tour='stats']",
    title: "Your Stats 📊",
    description: "Track your reliability score, krews joined, and nights out. High scores unlock perks!",
    position: "top",
  },
];

const STORAGE_KEY = "krew_tour_completed";

const GuidedTour = () => {
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      // Delay to let dashboard render
      const timer = setTimeout(() => setActive(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const positionTooltip = useCallback(() => {
    if (!active) return;
    const step = TOUR_STEPS[currentStep];
    const el = document.querySelector(step.target);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    // Highlight the element
    el.classList.add("tour-highlight");

    let style: React.CSSProperties = { position: "absolute", zIndex: 9999 };

    switch (step.position) {
      case "bottom":
        style.top = rect.bottom + scrollY + 12;
        style.left = Math.max(16, rect.left + scrollX + rect.width / 2 - 160);
        break;
      case "top":
        style.top = rect.top + scrollY - 180;
        style.left = Math.max(16, rect.left + scrollX + rect.width / 2 - 160);
        break;
      case "right":
        style.top = rect.top + scrollY + rect.height / 2 - 60;
        style.left = rect.right + scrollX + 12;
        break;
      case "left":
        style.top = rect.top + scrollY + rect.height / 2 - 60;
        style.left = rect.left + scrollX - 332;
        break;
    }

    // Ensure within viewport
    style.left = Math.min(Number(style.left), window.innerWidth - 340);
    style.left = Math.max(16, Number(style.left));

    setTooltipStyle(style);
  }, [active, currentStep]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener("resize", positionTooltip);
    window.addEventListener("scroll", positionTooltip);
    return () => {
      window.removeEventListener("resize", positionTooltip);
      window.removeEventListener("scroll", positionTooltip);
      // Remove all highlights
      document.querySelectorAll(".tour-highlight").forEach(el => el.classList.remove("tour-highlight"));
    };
  }, [positionTooltip]);

  const next = () => {
    const prevEl = document.querySelector(TOUR_STEPS[currentStep].target);
    prevEl?.classList.remove("tour-highlight");

    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(s => s + 1);
    } else {
      complete();
    }
  };

  const prev = () => {
    const prevEl = document.querySelector(TOUR_STEPS[currentStep].target);
    prevEl?.classList.remove("tour-highlight");
    setCurrentStep(s => Math.max(0, s - 1));
  };

  const complete = () => {
    document.querySelectorAll(".tour-highlight").forEach(el => el.classList.remove("tour-highlight"));
    localStorage.setItem(STORAGE_KEY, "true");
    setActive(false);
  };

  if (!active) return null;

  const step = TOUR_STEPS[currentStep];

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998] bg-black/50" onClick={complete} />

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={tooltipStyle}
          className="w-80 rounded-2xl border border-primary/30 bg-card p-5 shadow-2xl"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs font-medium text-muted-foreground">
                Step {currentStep + 1} of {TOUR_STEPS.length}
              </span>
            </div>
            <button onClick={complete} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          <h3 className="mb-1 text-lg font-bold text-foreground">{step.title}</h3>
          <p className="mb-4 text-sm text-muted-foreground">{step.description}</p>

          <div className="flex items-center justify-between">
            <button
              onClick={complete}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Skip tour
            </button>
            <div className="flex gap-2">
              {currentStep > 0 && (
                <button
                  onClick={prev}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                  <ArrowLeft className="h-3 w-3" /> Back
                </button>
              )}
              <button
                onClick={next}
                className="flex items-center gap-1 rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground"
              >
                {currentStep === TOUR_STEPS.length - 1 ? "Finish" : "Next"}
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Progress dots */}
          <div className="mt-3 flex justify-center gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-1.5 rounded-full transition-all ${i === currentStep ? "w-4 bg-primary" : i < currentStep ? "bg-primary/50" : "bg-muted"}`}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default GuidedTour;
